import { asStableCallback } from "@bezoned/shared/hooks/use-stable-callback.js";
import type { OfficePresetManifest } from "@bezoned/shared/schemas/office/office-preset-manifest.schema.js";
import {
  Dropdown,
  Field,
  Option,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "@fluentui/react-components";

import type { CurrentTableDisplayItem } from "../team-edit-theme-switch-dialog.helpers.js";
import { DO_NOT_USE } from "../team-edit-theme-switch-dialog.helpers.js";

type NewPresetTable = OfficePresetManifest["tables"][number];

const getDropdownValueLabel = (
  selectedId: string,
  newPresetTables: NewPresetTable[]
): string => {
  if (selectedId === DO_NOT_USE) {
    return "Do not use";
  }

  return (
    newPresetTables.find((t) => t.id === selectedId)?.defaultName ??
    "Select a table"
  );
};

// --- Pairing row component ---

interface PairingRowProps {
  index: number;
  currentDisplayName: string | null;
  selectedId: string;
  isDuplicate: boolean;
  newPresetTables: NewPresetTable[];
  onSelectionChange: (rowIndex: number, value: string) => void;
}

const PairingRow = ({
  index,
  currentDisplayName,
  selectedId,
  isDuplicate,
  newPresetTables,
  onSelectionChange,
}: PairingRowProps) => (
  <TableRow>
    <TableCell className="w-[50%] overflow-hidden text-ellipsis whitespace-nowrap">
      {currentDisplayName ?? (
        <span className="text-neutral-400 italic">n/a</span>
      )}
    </TableCell>
    <TableCell className="w-[50%]">
      <Field
        validationMessage={
          isDuplicate
            ? "This table is already selected. Please select another one."
            : null
        }
        validationState={isDuplicate ? "error" : "none"}
      >
        <Dropdown
          button={{ className: "w-full !min-w-0" }}
          className="w-full !min-w-0"
          onOptionSelect={asStableCallback((_event, data) => {
            if (data.optionValue) {
              onSelectionChange(index, data.optionValue);
            }
          })}
          selectedOptions={selectedId ? [selectedId] : []}
          value={getDropdownValueLabel(selectedId, newPresetTables)}
        >
          <Option value={DO_NOT_USE}>Do not use</Option>
          {newPresetTables.map((table) => (
            <Option key={table.id} value={table.id}>
              {table.defaultName}
            </Option>
          ))}
        </Dropdown>
      </Field>
    </TableCell>
  </TableRow>
);

// --- Pairing step content ---

interface PairingStepContentProps {
  currentTableDisplayData: CurrentTableDisplayItem[];
  rowCount: number;
  rowSelections: Record<number, string>;
  initialRowSelections: Record<number, string>;
  duplicateNewTableIds: Set<string>;
  newPresetTables: NewPresetTable[];
  onSelectionChange: (rowIndex: number, value: string) => void;
}

export const PairingStepContent = ({
  currentTableDisplayData,
  rowCount,
  rowSelections,
  initialRowSelections,
  duplicateNewTableIds,
  newPresetTables,
  onSelectionChange,
}: PairingStepContentProps) => (
  <>
    <p className="mb-2 text-sm">
      Your current office design has {currentTableDisplayData.length}{" "}
      {currentTableDisplayData.length === 1 ? "table" : "tables"} and your new
      office will have {newPresetTables.length}{" "}
      {newPresetTables.length === 1 ? "table" : "tables"}. Please select how old
      tables should be mapped to new ones.
    </p>
    <Table
      aria-label="Pair current tables to new tables"
      className="w-full table-fixed"
    >
      <TableHeader>
        <TableRow>
          <TableHeaderCell className="w-[50%]">Current Office</TableHeaderCell>
          <TableHeaderCell className="w-[50%]">New Office</TableHeaderCell>
        </TableRow>
      </TableHeader>
      <TableBody>
        {Array.from({ length: rowCount }).map((_, index) => {
          const selectedId =
            rowSelections[index] ?? initialRowSelections[index] ?? "";
          const isDuplicate =
            selectedId !== "" &&
            selectedId !== DO_NOT_USE &&
            duplicateNewTableIds.has(selectedId);

          return (
            <PairingRow
              currentDisplayName={
                currentTableDisplayData.at(index)?.displayName ?? null
              }
              index={index}
              isDuplicate={isDuplicate}
              key={`pairing-row-${String(index)}`}
              newPresetTables={newPresetTables}
              onSelectionChange={onSelectionChange}
              selectedId={selectedId}
            />
          );
        })}
      </TableBody>
    </Table>
  </>
);
